import {
  Component,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  HostListener
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription, firstValueFrom } from 'rxjs';
import * as showdown from 'showdown';
import { v4 as uuidv4 } from 'uuid';

import { UiService, UiVideoJobUpdate } from '../../gen2ai/ui.service';
import { ChatFireService } from '../../gen2ai/chatfire.service';
import { Message } from '../../models/chat.model';

type VideoProviderId = 'veo31' | 'grokvideo';

interface VideoProviderOption {
  id: VideoProviderId;
  name: string;
  label: string;
}

@Component({
  selector: 'app-videochat',
  templateUrl: './video-chat.component.html',
  styleUrls: ['./video-chat.component.scss']
})
export class VideoChatComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('imageInput') imageInput?: ElementRef<HTMLInputElement>;
  @ViewChild('videoEl') videoEl?: ElementRef<HTMLVideoElement>;

  form: FormGroup;

  currentMessages: Message[] = [];
  isLoading = false;

  userInput = '';
  selectedImageFile: File | null = null;
  selectedImagePreview: string | null = null;
  messageHistory: string[] = [];
  historyIndex = -1;

  // menu
  showVideoProviderMenu = false;
  readonly videoProviders: VideoProviderOption[] = [
    { id: 'veo31', name: 'Veo 3.1', label: 'Google Veo 3.1' },
    { id: 'grokvideo', name: 'Grok Video', label: 'xAI Grok Video' }
  ];
  selectedProviderId: VideoProviderId = 'veo31';
  selectedProviderName = this.videoProviders[0].label;

  converter: showdown.Converter;
  private subs: Subscription[] = [];
  private videoJobSub?: Subscription;

  // embed controls state
  isVideoOpen = false;
  activeVideoUrl: string | null = null;
  activeThumbUrl: string | null = null;
  activeDownloadUrl: string | null = null;
  activeVideoTitle: string | null = null;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private uiService: UiService,
    private chatService: ChatFireService
  ) {
    this.form = this.fb.group({
      aspectRatio: ['16:9'],
      seconds: [8]
    });

    this.converter = new showdown.Converter({
      tables: true,
      simplifiedAutoLink: true,
      strikethrough: true,
      tasklists: true,
      ghCodeBlocks: true,
      smoothLivePreview: true
    });
  }

  ngOnInit(): void {
    this.subs.push(
      this.chatService.currentConversation$.subscribe(conv => {
        this.currentMessages = conv?.messages ?? [];
        this.scrollToBottomIfNeeded();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.videoJobSub?.unsubscribe();
  }

  // ---------------- Menu ----------------
  toggleVideoProviderMenu(): void {
    this.showVideoProviderMenu = !this.showVideoProviderMenu;
  }

  selectProvider(provider: VideoProviderOption): void {
    this.selectedProviderId = provider.id;
    this.selectedProviderName = provider.label;
    this.showVideoProviderMenu = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target) return;
    if (!target.closest('.video-provider-menu') && !target.closest('#videoProviderBtn')) {
      this.showVideoProviderMenu = false;
      this.cdr.markForCheck();
    }
  }

  // ---------------- File upload ----------------
  triggerImageUpload(): void {
    this.imageInput?.nativeElement?.click();
  }

  async onImageSelected(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    if (!/^image\//i.test(file.type)) {
      this.pushSystemMessage('Please upload a valid image file.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      this.pushSystemMessage('Image too large. Please keep under 8MB.');
      return;
    }

    this.selectedImageFile = file;
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read image.'));
      reader.onload = () => resolve(String(reader.result || ''));
      reader.readAsDataURL(file);
    });
    this.selectedImagePreview = dataUrl;
    this.cdr.markForCheck();
  }

  clearImage(): void {
    this.selectedImageFile = null;
    this.selectedImagePreview = null;
    if (this.imageInput?.nativeElement) this.imageInput.nativeElement.value = '';
  }

  // ---------------- Video overlay controls ----------------
  openVideo(
    url: string,
    thumbUrl?: string | null,
    title?: string | null
  ): void {
    this.isVideoOpen = true;
    this.activeVideoUrl = url;
    this.activeThumbUrl = thumbUrl || null;
    this.activeDownloadUrl = url; // MP4 download URL (same as stream for now)
    this.activeVideoTitle = title || null;
    this.cdr.markForCheck();

    setTimeout(() => {
      const el = this.videoEl?.nativeElement;
      if (el) {
        el.load();
        el.play().catch(() => {});
      }
    }, 0);
  }

  closeVideo(): void {
    const el = this.videoEl?.nativeElement;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    this.isVideoOpen = false;
    this.activeVideoUrl = null;
    this.activeThumbUrl = null;
    this.activeDownloadUrl = null;
    this.activeVideoTitle = null;
    this.cdr.markForCheck();
  }

  togglePlayPause(): void {
    const el = this.videoEl?.nativeElement;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  }

  // ---------------- Send ----------------
  async sendVideoRequest(): Promise<void> {
    const prompt = String(this.userInput || '').trim();
    if (!prompt) return;

    if (this.selectedImageFile && !this.providerSupportsImages(this.selectedProviderId)) {
      this.pushSystemMessage(`${this.selectedProviderName} does not support reference images yet.`);
      this.clearImage();
      return;
    }

    try {
      // Add user message to conversation (reuse existing store)
      await firstValueFrom(this.chatService.sendMessage(
        `🎬 **Video Request (${this.selectedProviderName})**\n\n${prompt}`,
        'chatgpt5',
        false
      ));

      this.isLoading = true;
      this.scrollToBottomIfNeeded();
      this.cdr.markForCheck();

      if (prompt) {
        this.messageHistory.push(prompt);
        this.historyIndex = -1;
      }

      const aspectRatio = this.form.get('aspectRatio')?.value || '16:9';
      const seconds = Number(this.form.get('seconds')?.value || 8);

      const job$ = this.getVideoJobStream({
        prompt,
        seconds,
        aspectRatio
      });

      this.videoJobSub?.unsubscribe();
      let jobMessage: Message | null = null;

      this.videoJobSub = job$.subscribe({
        next: (job) => {
          const updated = this.upsertVideoJobMessage(job, jobMessage);
          jobMessage = updated;

          if (job.status === 'completed' || job.status === 'failed') {
            this.isLoading = false;
            this.userInput = '';
            this.clearImage();
          }

          if (job.status === 'failed') {
            this.pushSystemMessage(`Video job failed: ${job.error || 'Unknown error'}`);
          }

          this.scrollToBottomIfNeeded();
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.isLoading = false;
          this.cdr.markForCheck();
          this.pushSystemMessage(err?.message || 'Video request failed.');
        }
      });
    } catch (e: any) {
      this.isLoading = false;
      this.cdr.markForCheck();
      this.pushSystemMessage(e?.message || 'Video request failed.');
    }
  }

  // ---------------- Messages helpers ----------------
  private pushSystemMessage(text: string) {
    const msg: Message = {
      id: uuidv4(),
      role: 'assistant',
      content: `⚠️ ${text}`,
      timestamp: new Date(),
      modelId: 'system',
      modelDisplayName: 'System'
    };
    const conv = (this.chatService as any).currentConversationSubject?.value;
    if (conv) {
      conv.messages.push(msg);
      this.chatService.updateConversation(conv).subscribe();
    }
  }

  private upsertVideoJobMessage(job: UiVideoJobUpdate, existing?: Message | null): Message {
    const providerName = this.selectedProviderName;
    const conv = (this.chatService as any).currentConversationSubject?.value;
    if (!conv) return existing || ({} as Message);

    let msg = existing;
    if (!msg) {
      msg = {
        id: uuidv4(),
        role: 'assistant',
        content: `🕊️ **Video job started**\n\n- Provider: **${providerName}**\n- Status: **${job.status}**\n- Job ID: \`${job.id}\``,
        timestamp: new Date(),
        modelId: 'video',
        modelDisplayName: 'Video Engine',
        videoJobId: job.id,
        videoStatus: job.status,
        provider: this.selectedProviderId
      } as any;
      conv.messages.push(msg);
    } else {
      (msg as any).videoJobId = job.id;
      (msg as any).videoStatus = job.status;
    }

    if (job.thumbnailUrl) (msg as any).videoThumbUrl = job.thumbnailUrl;
    if (job.videoUrl) (msg as any).videoUrl = job.videoUrl;

    if (job.status === 'completed' && job.videoUrl) {
      (msg as any).videoTitle = (job as any).title ?? 'Generated video';
      msg.content = `✅ **Your video is ready**\n\nProvider: **${providerName}**`;
    } else if (job.status === 'failed') {
      msg.content = `❌ **Video generation failed**\n\nProvider: **${providerName}**`;
    } else {
      msg.content = `🕊️ **Video job in progress**\n\n- Provider: **${providerName}**\n- Status: **${job.status}**\n- Job ID: \`${job.id}\``;
    }

    this.chatService.updateConversation(conv).subscribe();
    return msg;
  }

  convertMarkdown(md: string): string {
    try {
      return this.converter.makeHtml(md || '');
    } catch {
      return md || '';
    }
  }

  scrollToBottomIfNeeded() {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    }, 0);
  }

  onKeyDown(e: KeyboardEvent) {
    const el = e.target as HTMLTextAreaElement | null;
    if (!el) return;

    if (e.key === 'ArrowUp' && !e.shiftKey) {
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;

      if (start === 0 && end === 0) {
        e.preventDefault();
        this.recallPreviousMessage();
      }
      return;
    }

    if (e.key === 'ArrowDown' && !e.shiftKey) {
      const len = el.value?.length ?? 0;
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;

      if (start === len && end === len) {
        e.preventDefault();
        this.recallNextMessage();
      }
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendVideoRequest();
    }
  }

  recallPreviousMessage(): void {
    if (this.messageHistory.length === 0) return;
    if (this.historyIndex === -1) {
      this.historyIndex = this.messageHistory.length - 1;
    } else if (this.historyIndex > 0) {
      this.historyIndex--;
    }
    this.userInput = this.messageHistory[this.historyIndex];
  }

  recallNextMessage(): void {
    if (this.messageHistory.length === 0 || this.historyIndex === -1) return;
    if (this.historyIndex < this.messageHistory.length - 1) {
      this.historyIndex++;
      this.userInput = this.messageHistory[this.historyIndex];
    } else {
      this.historyIndex = -1;
      this.userInput = '';
    }
  }

  recallLastMessage(): void {
    if (this.messageHistory.length === 0) return;
    this.userInput = this.messageHistory[this.messageHistory.length - 1];
    this.historyIndex = this.messageHistory.length - 1;
  }

  getVideoUrl(msg: Message): string | null {
    return (msg as any).videoUrl || msg.video || null;
  }

  getVideoThumbUrl(msg: Message): string | null {
    return (msg as any).videoThumbUrl || msg.thumbnail || null;
  }

  getVideoTitle(msg: Message): string | null {
    return (msg as any).videoTitle || (msg as any).title || null;
  }

  private providerSupportsImages(providerId: VideoProviderId): boolean {
    return false;
  }

  private normalizeAspectRatio(providerId: VideoProviderId, aspectRatio: string): string {
    if (providerId === 'veo31') {
      return aspectRatio === '9:16' ? '9:16' : '16:9';
    }
    return aspectRatio;
  }

  private getVideoJobStream(opts: { prompt: string; seconds: number; aspectRatio: string }) {
    const aspectRatio = this.normalizeAspectRatio(this.selectedProviderId, opts.aspectRatio);
    if (this.selectedProviderId === 'veo31') {
      return this.uiService.veo31VideoGenUi({
        prompt: opts.prompt,
        seconds: opts.seconds,
        aspectRatio: aspectRatio as '16:9' | '9:16'
      });
    }
    if (this.selectedProviderId === 'grokvideo') {
      return this.uiService.grokVideoGenUi({
        prompt: opts.prompt,
        seconds: opts.seconds,
        aspectRatio
      });
    }
    return this.uiService.veo31VideoGenUi({
      prompt: opts.prompt,
      seconds: opts.seconds,
      aspectRatio: aspectRatio as '16:9' | '9:16'
    });
  }
}
